import type { RefAttributes } from "react";
import { useMemo } from "react";
import type { TextElementType, TextProps } from "../Text/Text";
import { Text } from "../Text/Text";

export type HTMLElementType = "span" | "div";

const isHTML = (text: string) => /^\s*<[a-z]/i.test(text);

export interface TextOrHTMLProps
  extends Omit<TextProps, "children">, RefAttributes<TextElementType> {
  /** HTML string to be rendered with dangerouslySetInnerHTML */
  text: string;
  /** Custom class name for the dangerous HTML element */
  htmlClassName?: string;
  /** HTML element type for the dangerous HTML element */
  htmlElementType?: HTMLElementType;
  /** If true, wraps the dangerous HTML element inside a Text component */
  textWrapper?: boolean;
  /** If true, auto-detects whether text is HTML or plain text to determine wrapping and element type */
  autoDetect?: boolean;
}

const TextOrHTML = ({
  ref,
  text,
  htmlClassName,
  htmlElementType,
  textWrapper = false,
  autoDetect = false,
  ...textProps
}: TextOrHTMLProps) => {
  const textIsHTML = autoDetect && isHTML(text);
  const shouldWrapInText = autoDetect
    ? textWrapper || !textIsHTML
    : textWrapper;
  const resolvedElementType = htmlElementType ?? (textIsHTML ? "div" : "span");
  const DangerousComponent = resolvedElementType;

  // Memoize the dangerouslySetInnerHTML object to prevent unnecessary re-renders
  // See: https://github.com/facebook/react/issues/31660
  const dangerousHTML = useMemo(() => ({ __html: text }), [text]);

  const dangerousElement = (
    <DangerousComponent
      className={htmlClassName}
      dangerouslySetInnerHTML={dangerousHTML}
    />
  );

  if (shouldWrapInText) {
    return (
      <Text ref={ref} {...textProps}>
        {dangerousElement}
      </Text>
    );
  }

  return dangerousElement;
};

TextOrHTML.displayName = "TextOrHTML";
export { TextOrHTML };
